import {
  useEffect, useCallback, useRef, useState,
} from 'react';
import * as signalR from '@microsoft/signalr';
import { LocalStorageService, MessageService } from 'services';
import useLogout from 'hooks/useLogout';
import { useTranslation } from 'react-i18next';
import { AppConstants } from 'constants/index';
import Config from 'config';

const { isBrRegulation } = Config;

const maxConnectionAttempts = 3;
const url = [AppConstants.api.NotificationAdmin, AppConstants.api.Hubs, AppConstants.api.NotificationHub].join('/');

const useConnection = () => {
  const { logout } = useLogout();
  const { t } = useTranslation();

  const [connection, setConnection] = useState(null);
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef(null);

  const buildConnection = useCallback(() => {
    const auth = LocalStorageService.get('auth');
    if (!auth?.access_token || isBrRegulation) return null;

    return new signalR.HubConnectionBuilder()
      .withUrl(`${Config.baseURL}${url}?access_token=${auth.access_token}`, {
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets,
      })
      .withAutomaticReconnect([0, 2000, 5000])
      .configureLogging(signalR.LogLevel.Information)
      .build();
  }, []);

  const startConnection = useCallback(async (conn) => {
    try {
      await conn.start();
      retryCountRef.current = 0;
    } catch (error) {
      if (error?.statusCode !== 401) {
        if (retryCountRef.current >= maxConnectionAttempts) {
          return;
        }

        retryCountRef.current += 1;

        retryTimeoutRef.current = setTimeout(() => {
          startConnection(conn);
        }, 1000);
      }
    }
  }, []);

  const connect = useCallback(() => {
    const conn = buildConnection();
    if (!conn) return;

    conn.on('ReceiveMessage', (userId, mess) => {
      switch (mess) {
      case 'LogoutOnDisable':
        MessageService.error({
          content: t('notification:accountDisabled'),
          onClose: logout,
        });
        break;

      case 'LogoutOnDelete':
        MessageService.error({
          content: t('notification:accountDeleted'),
          onClose: logout,
        });
        break;
      default:
        break;
      }
    });

    setConnection(conn);
    startConnection(conn);
  }, [buildConnection, logout, startConnection, t]);

  const restartConnection = () => {
    retryCountRef.current = 0;
    setConnection(null);
  };

  useEffect(() => {
    if (!connection) {
      connect();
    }

    return () => {
      clearTimeout(retryTimeoutRef.current);
      if (connection) {
        connection.off('ReceiveMessage');
        connection.stop();
      }
    };
  }, [connection, connect]);

  return {
    restartConnection,
  };
};

export default useConnection;
